// RapidCaseSelector.js
// Props:
//   cases           array of case rows                          (required)
//   marked          array of CaseIds marked "review later"      (required)
//   loading         bool                                        (required)
//   error           string | null                                (required)
//   onSelectCase    (caseRow) => void                           (required)
//   onToggleMark    (caseId) => void                            (required)
//   onRetry         () => void — refetch after an error         (optional)
//   onReviewGrade   (caseRow) => void — Review column           (optional)
//   onOpenSettings  () => void — opens the settings modal        (optional)
//
//   Display/filter settings — these are persisted server-side on
//   RapidCcsSettings by the parent (RapidCcsApp), so they survive across
//   sessions. This component just reflects the current values and reports
//   changes back up via onFilterChange; it doesn't own or persist them.
//   showDiagnosis    bool  — RapidCcsSettings.ShowDiagnosis    (optional)
//   hideCaseTitle    bool  — RapidCcsSettings.HideCaseTitle    (optional)
//   hideCategory     bool  — RapidCcsSettings.HideCategory     (optional)
//   hideCompleted    bool  — RapidCcsSettings.HideCompleted    (optional)
//   hideIncomplete   bool  — RapidCcsSettings.HideIncomplete   (optional)
//   reviewLaterOnly  bool  — RapidCcsSettings.ReviewLaterOnly  (optional)
//   onFilterChange  (settingKey, value) => void — called with the
//                   RapidCcsSettings field name (e.g. "ShowDiagnosis") and
//                   the new boolean value whenever one of the above toggles
//                   is changed                                  (optional)

import React from "react";

// Map category id -> label. Adjust to match the real category table if needed.
const CATEGORY_LABELS = {
  1: "Cardiology",
  2: "Neurology",
  3: "OB/GYN",
  4: "Psychiatry",
  5: "Emergency",
  6: "Pediatrics",
};
function categoryName(id) {
  return CATEGORY_LABELS[id] || "Category " + id;
}

const SORTABLE = {
  ID: function (c) {
    return c.ID;
  },
  TITLE: function (c) {
    return (c.TITLE || "").toLowerCase();
  },
  CATEGORY: function (c) {
    return categoryName(c.CASECATEGORYID).toLowerCase();
  },
  DIAGNOSIS: function (c) {
    return (c.DIAGNOSIS || "").toLowerCase();
  },
  COMPLETED: function (c) {
    return c.COMPLETED ? 1 : 0;
  },
  AVG: function (c) {
    return typeof c.AvgGradeForCase === "number" ? c.AvgGradeForCase : -1;
  },
};

class RapidCaseSelector extends React.Component {
  constructor(props) {
    super(props);

    // Local UI state only — the data itself lives in RapidCcsApp, and the
    // persisted display/filter toggles (showDiagnosis, hideCategory, etc.)
    // live in props now (see onFilterChange doc above) so they survive
    // across sessions via RapidCcsSettings.
    this.state = {
      search: "",
      sortKey: "ID",
      sortDir: "asc",

      // Category filter — empty array means "show all". Not persisted;
      // there's no RapidCcsSettings field for it (yet).
      activeCats: [],
    };
  }

  // ── Derived data (recomputed per render; lists are small) ────────────────

  isMarked = (id) => {
    return this.props.marked.indexOf(id) !== -1;
  };

  getAllCategories() {
    const ids = {};
    this.props.cases.forEach(function (c) {
      ids[c.CASECATEGORYID] = true;
    });
    return Object.keys(ids)
      .map(Number)
      .sort(function (a, b) {
        return a - b;
      });
  }

  getVisible() {
    const { search, activeCats, sortKey, sortDir } = this.state;
    const { hideCompleted, hideIncomplete, reviewLaterOnly } = this.props;

    const term = search.trim().toLowerCase();
    let rows = this.props.cases.filter((c) => {
      // search by diagnosis OR title OR id
      if (term) {
        const hay =
          (c.DIAGNOSIS || "").toLowerCase() +
          " " +
          (c.TITLE || "").toLowerCase() +
          " " +
          String(c.ID);
        if (hay.indexOf(term) === -1) return false;
      }
      if (hideCompleted && c.COMPLETED) return false;
      if (hideIncomplete && !c.COMPLETED) return false;
      if (reviewLaterOnly && !this.isMarked(c.ID)) return false;
      if (activeCats.length > 0 && activeCats.indexOf(c.CASECATEGORYID) === -1)
        return false;
      return true;
    });

    const getVal = SORTABLE[sortKey] || SORTABLE.ID;
    rows = rows.slice().sort(function (a, b) {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return rows;
  }

  // ── Handlers (local UI state only) ───────────────────────────────────────

  handleStarClick = (caseId, e) => {
    e.stopPropagation(); // don't open the case when starring it
    this.props.onToggleMark(caseId);
  };

  setFilter = (settingKey, value) => {
    if (this.props.onFilterChange) {
      this.props.onFilterChange(settingKey, value);
    }
  };

  toggleCat = (id) => {
    this.setState(function (prevState) {
      const has = prevState.activeCats.indexOf(id) !== -1;
      return {
        activeCats: has
          ? prevState.activeCats.filter(function (x) {
              return x !== id;
            })
          : prevState.activeCats.concat([id]),
      };
    });
  };

  setSort = (key) => {
    if (this.state.sortKey === key) {
      this.setState(function (prevState) {
        return { sortDir: prevState.sortDir === "asc" ? "desc" : "asc" };
      });
    } else {
      this.setState({ sortKey: key, sortDir: "asc" });
    }
  };

  sortArrow(key) {
    if (this.state.sortKey !== key) return null;
    return (
      <span className="rccs-sort">
        {this.state.sortDir === "asc" ? "▲" : "▼"}
      </span>
    );
  }

  // ── Render ───────────────────────────────────────────────────────────────

  render() {
    const {
      cases,
      loading,
      error,
      onSelectCase,
      onReviewGrade,
      onRetry,
      onOpenSettings,
      showDiagnosis,
      hideCaseTitle,
      hideCategory,
      hideCompleted,
      hideIncomplete,
      reviewLaterOnly,
    } = this.props;
    const { search, activeCats } = this.state;
    if (loading) {
      return (
        <div className="rccs">
          <div className="rccs-loading">
            <div className="rccs-spinner" />
            Loading cases…
          </div>
        </div>
      );
    }
    if (error) {
      return (
        <div className="rccs">
          <div className="rccs-error">
            {error}
            {onRetry && (
              <button type="button" className="rccs-btn" onClick={onRetry}>
                Try again
              </button>
            )}
          </div>
        </div>
      );
    }

    const allCategories = this.getAllCategories();
    const visible = this.getVisible();

    return (
      <div className="rccs">
        <div className="rccs-selector">
          <div className="rccs-selector__head">
            <h1 className="rccs-selector__title">
              <small>Rapid CCS</small>
              Choose a case
            </h1>
            <span className="rccs-selector__count">
              {visible.length} of {cases.length} cases
            </span>
          </div>

          <div className="rccs-toolbar">
            <div className="rccs-search">
              <span className="rccs-search__icon" aria-hidden="true">
                ⌕
              </span>
              <input
                type="text"
                placeholder="Search by diagnosis, title, or case #…"
                value={search}
                onChange={(e) => {
                  this.setState({ search: e.target.value });
                }}
                aria-label="Search cases"
              />
            </div>
          </div>

          <div
            className="rccs-options"
            role="group"
            aria-label="Display options"
          >
            <label className="rccs-check">
              <input
                type="checkbox"
                checked={showDiagnosis}
                onChange={(e) => {
                  this.setFilter("ShowDiagnosis", e.target.checked);
                }}
              />
              Show diagnosis
            </label>
            <label className="rccs-check">
              <input
                type="checkbox"
                checked={hideCaseTitle}
                onChange={(e) => {
                  this.setFilter("HideCaseTitle", e.target.checked);
                }}
              />
              Hide case title
            </label>
            <label className="rccs-check">
              <input
                type="checkbox"
                checked={hideCategory}
                onChange={(e) => {
                  this.setFilter("HideCategory", e.target.checked);
                }}
              />
              Hide category
            </label>
            <label className="rccs-check">
              <input
                type="checkbox"
                checked={hideCompleted}
                onChange={(e) => {
                  this.setFilter("HideCompleted", e.target.checked);
                }}
              />
              Hide completed
            </label>
            <label className="rccs-check">
              <input
                type="checkbox"
                checked={hideIncomplete}
                onChange={(e) => {
                  this.setFilter("HideIncomplete", e.target.checked);
                }}
              />
              Hide incomplete
            </label>
            <label className="rccs-check">
              <input
                type="checkbox"
                checked={reviewLaterOnly}
                onChange={(e) => {
                  this.setFilter("ReviewLaterOnly", e.target.checked);
                }}
              />
              Review later only
            </label>
            {onOpenSettings && (
              <button
                type="button"
                className="rccs-btn rccs-btn--sm rccs-btn-end"
                onClick={onOpenSettings}
              >
                Settings
              </button>
            )}
          </div>

          {allCategories.length > 0 && (
            <div
              className="rccs-cats"
              role="group"
              aria-label="Filter by category"
            >
              {allCategories.map((id) => {
                const on = activeCats.indexOf(id) !== -1;
                return (
                  <button
                    key={id}
                    type="button"
                    className={
                      "rccs-cat-chip" + (on ? " rccs-cat-chip--on" : "")
                    }
                    onClick={() => {
                      this.toggleCat(id);
                    }}
                    aria-pressed={on}
                  >
                    {categoryName(id)}
                  </button>
                );
              })}
            </div>
          )}

          <div className="rccs-table-wrap">
            <table className="rccs-table">
              <thead>
                <tr>
                  <th
                    onClick={() => {
                      this.setSort("ID");
                    }}
                  >
                    Case # {this.sortArrow("ID")}
                  </th>
                  {!hideCaseTitle && (
                    <th
                      onClick={() => {
                        this.setSort("TITLE");
                      }}
                    >
                      Title {this.sortArrow("TITLE")}
                    </th>
                  )}
                  {!hideCategory && (
                    <th
                      onClick={() => {
                        this.setSort("CATEGORY");
                      }}
                    >
                      Category {this.sortArrow("CATEGORY")}
                    </th>
                  )}
                  {showDiagnosis && (
                    <th
                      onClick={() => {
                        this.setSort("DIAGNOSIS");
                      }}
                    >
                      Diagnosis {this.sortArrow("DIAGNOSIS")}
                    </th>
                  )}
                  <th
                    onClick={() => {
                      this.setSort("COMPLETED");
                    }}
                  >
                    Status {this.sortArrow("COMPLETED")}
                  </th>
                  <th
                    onClick={() => {
                      this.setSort("AVG");
                    }}
                  >
                    Avg grade {this.sortArrow("AVG")}
                  </th>
                  {onReviewGrade && <th aria-label="Review grade">Review</th>}
                  <th aria-label="Review later">★</th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr>
                    <td className="rccs-empty" colSpan={8}>
                      No cases match the current filters.
                    </td>
                  </tr>
                )}
                {visible.map((c) => {
                  return (
                    <tr
                      key={c.ID}
                      onClick={() => {
                        onSelectCase(c);
                      }}
                    >
                      <td className="rccs-id" data-label="Case #">
                        {c.ID}
                      </td>
                      {!hideCaseTitle && <td data-label="Title">{c.TITLE}</td>}
                      {!hideCategory && (
                        <td data-label="Category">
                          {categoryName(c.CASECATEGORYID)}
                        </td>
                      )}
                      {showDiagnosis && (
                        <td className="rccs-diag" data-label="Diagnosis">
                          {c.DIAGNOSIS}
                        </td>
                      )}
                      <td data-label="Status">
                        {c.COMPLETED ? (
                          <span className="rccs-pill rccs-pill--done">
                            Completed
                          </span>
                        ) : (
                          <span className="rccs-pill rccs-pill--todo">
                            Not started
                          </span>
                        )}
                      </td>
                      <td className="rccs-grade" data-label="Avg grade">
                        {typeof c.AvgGradeForCase === "number"
                          ? c.AvgGradeForCase.toFixed(2)
                          : "—"}
                      </td>
                      {onReviewGrade && (
                        <td data-label="Review">
                          {c.COMPLETED ? (
                            <button
                              type="button"
                              className="rccs-btn rccs-btn--sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                onReviewGrade(c);
                              }}
                            >
                              Review
                            </button>
                          ) : (
                            <span
                              style={{
                                color: "var(--ink-faint)",
                                fontSize: 12,
                              }}
                            >
                              —
                            </span>
                          )}
                        </td>
                      )}
                      <td data-label="Mark for review" className="rccs-td-star">
                        <button
                          type="button"
                          className={
                            "rccs-star" +
                            (this.isMarked(c.ID) ? " rccs-star--on" : "")
                          }
                          onClick={(e) => {
                            this.handleStarClick(c.ID, e);
                          }}
                          aria-label={
                            this.isMarked(c.ID)
                              ? "Remove review-later mark"
                              : "Mark for review later"
                          }
                          title="Review later"
                        >
                          {this.isMarked(c.ID) ? "★" : "☆"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }
}

export default RapidCaseSelector;
